<!-- components/pricing/PricingCalculator.vue -->
<template>
    <div class="my-8 sm:my-12 sm:mt-12 max-w-3xl mx-auto">
        <a name="calculator" id="calculator" class="anchor"></a>
        <div
            class="mt-2 border border-gray-700/20  rounded-3xl shadow-lg shadow-gray-700/20 bg-gradient-to-br from-gray-900 via-gray-900/95 to-blue-950/10 p-8 backdrop-blur-sm">
            <h2 class="mb-8 text-white">
                <span class="text-xl font-bold">Which plan is right for you?</span>
                <span class="text-xl font-normal ml-2">Use our pricing calculator to find out.</span>
            </h2>

            <!-- Calculator Form -->
            <form class="text-sm lg:text-base space-y-6 text-gray-400">
                <!-- Customers Input -->
                <div class="flex flex-wrap items-baseline lg:space-x-2">
                    <span>You have</span>
                    <input type="number" v-model.number="formData.customers"
                        class="w-24 bg-transparent border-b border-gray-700 focus:border-blue-500 text-white text-lg font-medium px-2 py-1 focus:outline-none" />
                    <span>customers.</span>
                </div>

                <!-- Connections Input -->
                <div class="flex flex-wrap items-baseline lg:space-x-2">
                    <span>Each customer has</span>
                    <input type="number" v-model.number="formData.connections" :min="1"
                        class="w-16 bg-transparent border-b border-gray-700 focus:border-blue-500 text-white text-lg font-medium px-2 py-1 focus:outline-none" />
                    <a href="#faq" @click="$emit('linkClick', 'connection')" class="text-gray-400 hover:text-blue-400">
                        {{ formData.connections !== 1 ? 'connections' : 'connection' }}
                    </a>
                    <span>on average.</span>
                </div>

                <!-- API Requests Input -->
                <div class="flex flex-wrap items-baseline lg:space-x-2">
                    <span>Each connection has</span>
                    <input type="number" v-model.number="formData.list_requests" :min="1"
                        class="w-16 bg-transparent border-b border-gray-700 focus:border-blue-500 text-white text-lg font-medium px-2 py-1 focus:outline-none" />

                    List {{ formData.requests !== 1 ? 'API requests' : 'API request' }}

                    per day on average (returning ~100 results each).
                </div>

                <div class="flex flex-wrap items-baseline lg:space-x-2">
                    <span>Each connection has</span>
                    <input type="number" v-model.number="formData.requests" :min="1"
                        class="w-16 bg-transparent border-b border-gray-700 focus:border-blue-500 text-white text-lg font-medium px-2 py-1 focus:outline-none" />

                    Retrieve/Create/Update/Delete {{ formData.requests !== 1 ? 'API requests' : 'API request' }}
                    per day on average.
                </div>

                <!-- Results -->
                <div class="pt-8 border-t border-gray-700/50">
                    <p class="text-gray-400">
                        On average, you will have
                        <span v-if="formData.connections > 1">
                            <a href="#faq" @click="$emit('linkClick', 'connection')"
                                class="text-white hover:text-blue-400">
                                {{ formatNumber(totalConnections) }} customer connections
                            </a>
                            with
                        </span>
                        <a href="#faq" @click="$emit('linkClick', 'apirequest')" class="text-white hover:text-blue-400">
                            {{ formatNumber(totalRequests) }} API requests
                        </a>
                        per month.
                    </p>

                    <div v-if="suggestedPlan.name !== 'Enterprise'" class="mt-6">
                        <p class="text-base">
                            Your best plan is
                            <span class="text-white font-semibold">{{ suggestedPlan.name }}</span>
                            for
                            <span class="text-white font-semibold">{{ formatMoney(suggestedPlan.cost, '$', 0, '')
                            }}/month</span>.
                            That's just
                            <span class="text-white font-semibold"> {{ formatMoney(suggestedPlan.cost /
                                formData.customers, '$', 2, '')
                            }}/customer/month</span>!
                        </p>
                        <p v-if="suggestedPlan.cost < 4000" class="mt-6 text-blue-400">
                            That is <span class="font-semibold">{{ formatNumber(100000 / suggestedPlan.cost / 12)
                            }}x</span> cheaper than 1 developer!
                        </p>
                    </div>
                </div>

                <!-- CTA Button -->
                <div class="pt-6">
                    <a href="https://app.unified.to"
                        class="inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold bg-blue-500 text-white hover:bg-blue-400 transition-colors">
                        <span>{{ buttonText }}</span>
                    </a>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { formatNumber, formatMoney } from '~/services/pricing';
import { PLANS } from '~/models/UnifiedInternal';

export default {
    data() {
        return {
            formData: {
                customers: 100,
                connections: 1,
                list_requests: 1,
                requests: 10
            }
        };
    },

    computed: {
        totalConnections() {
            return this.formData.connections * this.formData.customers;
        },

        totalRequests() {
            return this.totalConnections * ((this.formData.requests || 0) + (this.formData.list_requests || 0)) * 31;
        },

        suggestedPlan() {
            const connections = this.totalConnections;
            const requests = this.totalRequests;
            let splan = { name: '', description: '', cost: 0 };

            for (const plan of PLANS) {
                if (!plan.is_active) {
                    continue;
                }

                if (!plan.min_price) {
                    continue;
                }

                if (plan.max_connections > 0 && connections > plan.max_connections) {
                    continue;
                }

                if (plan.max_requests && requests > plan.max_requests) {
                    continue;
                }

                const cost = this.calculatePlanCost(plan);
                if (cost < splan.cost || !splan.cost) {
                    splan = { ...plan, cost };
                }
            }

            // If no plan was found (all were skipped) or cost exceeds threshold, suggest Enterprise
            if (!splan.name || splan.cost > 5000) {
                return {
                    name: 'Enterprise',
                    description: 'For high-volume enterprise needs',
                    cost: 0  // Ensures Enterprise messaging is shown
                };
            }

            return splan;
        },

        buttonText() {
            if (this.suggestedPlan.name === 'Enterprise') {
                return 'Contact us about Enterprise pricing';
            }
            if (['Start', 'Grow'].includes(this.suggestedPlan.name)) {
                return 'Start 30-day free trial';
            }
            return `Sign up with the ${this.suggestedPlan.name} plan`;
        }
    },

    methods: {
        calculatePlanCost(plan) {
            const requests = this.totalRequests;

            let cost = plan.min_price || 0;
            if (plan.max_requests && plan.price_per_mille_requests && requests > plan.max_requests) {
                cost += ((requests - plan.max_requests) / 1000) * plan.price_per_mille_requests;
            }

            return cost;
        },

        formatNumber,
        formatMoney
    }
};
</script>

<style scoped>
input[type="number"] {
    -moz-appearance: textfield;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
</style>